import { SmallSpinner } from "@autumn/ui/components/general/small-spinner";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@autumn/ui/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@autumn/ui/components/ui/popover";
import { cn } from "@autumn/ui/lib/utils";
import { CheckIcon, ChevronDownIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

export type SearchableSelectFooter =
	| ReactNode
	| ((props: { close: () => void }) => ReactNode);

export type SearchableSelectProps<T> = {
	value: string | null;
	onValueChange: (value: string) => void;
	options: T[];
	getOptionValue: (option: T) => string;
	getOptionLabel: (option: T) => string;
	/** Extra strings to match on, alongside the option's label and value. */
	getOptionSearchTerms?: (option: T) => (string | null | undefined)[];
	getOptionDisabled?: (option: T) => boolean;
	renderOption?: (option: T, isSelected: boolean) => ReactNode;
	renderValue?: (option: T | undefined) => ReactNode;
	placeholder?: string;
	searchable?: boolean;
	searchPlaceholder?: string;
	/** `null` renders no empty state at all — the search input stands alone. */
	emptyText?: ReactNode | null;
	disabled?: boolean;
	triggerClassName?: string;
	contentClassName?: string;
	defaultOpen?: boolean;
	/** Replaces the default value-and-chevron button, e.g. with an icon button. */
	trigger?: ReactNode;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	header?: ReactNode;
	footer?: SearchableSelectFooter;
	onSearchChange?: (search: string) => void;
	/**
	 * Whether to filter options locally as the user types. Defaults to off when
	 * `onSearchChange` is set, since the caller is usually searching server-side.
	 */
	shouldFilter?: boolean;
	isLoading?: boolean;
};

export function SearchableSelect<T>({
	value,
	onValueChange,
	options,
	getOptionValue,
	getOptionLabel,
	getOptionSearchTerms,
	getOptionDisabled,
	renderOption,
	renderValue,
	placeholder = "Select...",
	searchable = false,
	searchPlaceholder = "Search...",
	emptyText = "No results found",
	disabled = false,
	triggerClassName,
	contentClassName,
	defaultOpen = false,
	trigger,
	open: controlledOpen,
	onOpenChange,
	header,
	footer,
	onSearchChange,
	shouldFilter = !onSearchChange,
	isLoading = false,
}: SearchableSelectProps<T>) {
	const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
	const open = controlledOpen ?? uncontrolledOpen;
	const onOpenChangeRef = useRef(onOpenChange);
	onOpenChangeRef.current = onOpenChange;
	const setOpen = useCallback((next: boolean) => {
		setUncontrolledOpen(next);
		onOpenChangeRef.current?.(next);
	}, []);

	useEffect(() => {
		if (!defaultOpen) return;
		const timer = setTimeout(() => setOpen(true), 200);
		return () => clearTimeout(timer);
	}, [defaultOpen, setOpen]);

	const selectedOption = options.find((opt) => getOptionValue(opt) === value);

	const handleSelect = (option: T) => {
		if (getOptionDisabled?.(option)) return;
		onValueChange(getOptionValue(option));
		setOpen(false);
	};

	const defaultRenderValue = (option: T | undefined) => {
		if (!option)
			return <span className="text-tertiary-foreground">{placeholder}</span>;
		return <span>{getOptionLabel(option)}</span>;
	};

	const defaultRenderOption = (option: T, isSelected: boolean) => {
		const isDisabled = getOptionDisabled?.(option) ?? false;
		return (
			<>
				<span className="flex-1 truncate min-w-0">
					{getOptionLabel(option)}
				</span>
				{isSelected && !isDisabled && <CheckIcon className="size-4 shrink-0" />}
			</>
		);
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild disabled={disabled}>
				{trigger ?? (
					<button
						type="button"
						aria-expanded={open}
						aria-haspopup="listbox"
						disabled={disabled}
						className={cn(
							"flex items-center justify-between gap-2 w-full min-w-0 cursor-pointer text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50 rounded-lg",
							"input-base input-shadow-default input-state-open transition-all duration-150",
							triggerClassName,
						)}
					>
						<div className="flex items-center gap-2 min-w-0 flex-1">
							<span className="truncate min-w-0">
								{renderValue
									? renderValue(selectedOption)
									: defaultRenderValue(selectedOption)}
							</span>
						</div>
						<ChevronDownIcon className="size-4 shrink-0 opacity-50" />
					</button>
				)}
			</PopoverTrigger>
			<AnimatePresence>
				{open && (
					<PopoverContent
						align="start"
						className={cn(
							"w-(--anchor-width) p-0 z-200 rounded-md overflow-hidden",
							contentClassName,
						)}
						asChild
					>
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.3 }}
						>
							<Command
								className="bg-interactive-secondary"
								filter={
									!shouldFilter
										? () => 1
										: searchable
											? (optionValue, search) => {
													const option = options.find(
														(opt) => getOptionValue(opt) === optionValue,
													);
													if (!option) return 0;
													const searchLower = search.trim().toLowerCase();
													const haystack = [
														getOptionLabel(option),
														optionValue,
														...(getOptionSearchTerms?.(option) ?? []),
													];
													return haystack.some((term) =>
														term?.toLowerCase().includes(searchLower),
													)
														? 1
														: 0;
												}
											: undefined
								}
							>
								{searchable && (
									<CommandInput
										placeholder={searchPlaceholder}
										onValueChange={onSearchChange}
									/>
								)}
								{header}
								<CommandList>
									{(emptyText !== null || isLoading) && (
										<CommandEmpty className="text-tertiary-foreground">
											{isLoading ? (
												<div className="flex justify-center items-center py-2">
													<SmallSpinner size={14} />
												</div>
											) : (
												emptyText
											)}
										</CommandEmpty>
									)}
									<CommandGroup>
										{options.map((option) => {
											const optionValue = getOptionValue(option);
											const isSelected = optionValue === value;
											const isDisabled = getOptionDisabled?.(option) ?? false;

											return (
												<CommandItem
													key={optionValue}
													value={optionValue}
													onSelect={() => handleSelect(option)}
													disabled={isDisabled}
													className={cn(
														"min-w-0 cursor-pointer",
														isDisabled &&
															"text-subtle pointer-events-none opacity-50",
													)}
												>
													{renderOption
														? renderOption(option, isSelected)
														: defaultRenderOption(option, isSelected)}
												</CommandItem>
											);
										})}
									</CommandGroup>
								</CommandList>
							</Command>
							{typeof footer === "function"
								? footer({ close: () => setOpen(false) })
								: footer}
						</motion.div>
					</PopoverContent>
				)}
			</AnimatePresence>
		</Popover>
	);
}
